import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import SearchByField from 'Components/SearchByField'
import { AffiliateOrderFilters } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderFilters'
import { AffiliateOrderList } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderList'
import { useGetWalttiPosOrderFilteredSearchOptionsQuery } from 'Services/SalesManagement/Features/Order/orderApi'
import {
  fetchWalttiPosOrderList,
  type WalttiPosOrderParamShape
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { getFilterByDefaultDate, SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import useQuery from 'Util/getQueryParamsHook'
import type { SelectChangeEvent } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function WalttiPosOrderListPage() {
  const dispatch = useAppDispatch()
  const queryParams = useQuery()
  const navigate = useNavigate()

  const { walttiPosOrders, count, loading } = useTypedSelector(state => state.order)

  const { data: walttiPosOrderFilterChoices, isFetching: isWalttiPosOrderFilterOptionsFetching } =
    useGetWalttiPosOrderFilteredSearchOptionsQuery()

  const rows = walttiPosOrders

  const [searchText, setSearchText] = useState(queryParams.get('search') ?? '')

  const [filters, setFilters] = useState<Partial<WalttiPosOrderParamShape>>({
    operator_id: queryParams.get('operator_id') ?? '',
    sales_channel_name: queryParams.get('sales_channel_name') ?? '',
    payment_method: queryParams.get('payment_method') ?? '',
    refund_status: queryParams.get('refund_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })

  const defaultSearchFieldChoice = walttiPosOrderFilterChoices?.search_field_choices[0][0] ?? ''
  const initialSearchFieldChoice = queryParams.get('search_field') ?? defaultSearchFieldChoice
  const [searchField, setSearchField] = useState(initialSearchFieldChoice)

  const [params, setParams] = useState<WalttiPosOrderParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    search: queryParams.get('search') ?? '',
    search_field: initialSearchFieldChoice,
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1,
    operator_id: queryParams.get('operator_id') ?? '',
    sales_channel_name: queryParams.get('sales_channel_name') ?? '',
    payment_method: queryParams.get('payment_method') ?? '',
    refund_status: queryParams.get('refund_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })

  useEffect(() => {
    setSearchField(initialSearchFieldChoice)
  }, [walttiPosOrderFilterChoices])

  useEffect(() => {
    dispatch(fetchWalttiPosOrderList(params)).then(() => {
      setQueryParams()
    })
  }, [dispatch, params])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof WalttiPosOrderParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: '?' + queryString
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({
      ...prevState,
      ordering: `${isAsc ? '' : '-'}${property}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }
  const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearchField(value)
  }

  const handleSelectChange = (event: SelectChangeEvent, name: keyof WalttiPosOrderParamShape) => {
    const { value } = event.target
    const newValue = params[name] === value ? '' : value
    setFilters(prevState => ({ ...prevState, [name]: newValue }))
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setFilters(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const submitFilteredSearch = () => {
    setParams(prevState => ({
      ...prevState,
      ...filters,
      search_field: searchField,
      search: searchText,
      page: 1
    }))
  }

  const clearFilters = () => {
    setFilters(prevState => ({
      ...prevState,
      operator_id: '',
      sales_channel_name: '',
      payment_method: '',
      refund_status: '',
      created_after: '',
      created_before: ''
    }))
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Orders`
    },
    {
      title: t`Point of sale orders`
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{t`Point of sale orders`}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the point of sale orders section you can search for, filter and sort the orders made in the self service checkouts and kiosks. The order listing below is refreshed at regular intervals, so note that the latest orders are not listed here in real-time and the changes made to a single order are not updated in this list in real-time. Also note that orders older than one year are not included in the list.`}
        </Typography>
        <ListActions>
          <SearchByField
            searchText={searchText}
            handleSearchTextChange={handleSearchTextChange}
            handleSearch={submitFilteredSearch}
            searchLabelText={t`Search for point of sale orders`}
            searchHelperText={t`Search for travel card, external ID, travel account, email address, or Waltti ID`}
            searchField={searchField}
            isSearchFieldChoicesLoading={isWalttiPosOrderFilterOptionsFetching}
            searchFieldChoices={walttiPosOrderFilterChoices?.search_field_choices ?? []}
            handleSearchFieldChange={handleSearchFieldChange}
          />
        </ListActions>
      </Box>
      <AffiliateOrderFilters
        isFiltersLoading={isWalttiPosOrderFilterOptionsFetching}
        filterChoices={walttiPosOrderFilterChoices?.field_choices}
        currentFilters={filters}
        clearFilters={clearFilters}
        submitFilters={submitFilteredSearch}
        handleDatePickerChange={handleDatePickerChange}
        handleSelectChange={handleSelectChange}
      />
      <AffiliateOrderList
        rows={rows}
        loading={loading}
        params={params}
        handleRequestSort={handleRequestSort}
        handleChangePage={handleChangePage}
        count={count}
        singleUrl="walttipos-orders"
      />
    </Box>
  )
}
